import { useEffect, useRef, useMemo, useState } from "react";
import styled from "styled-components";
import { useLoaderData } from "react-router-dom";
import colors from "../../styles/colors";
import CommonWrapper from "../../components/ui_components/CommonComponentsStructureCss";
import { apiCreateArenaV2, apiListArenasByPage } from "../../api/arenaApi";
import CreateArenaModale from "../../components/admin/ArenaHandleFolder/CreateArenaModale";
import type { Arena } from "../../types";

const Container = styled.div`
    width: 80%;
    margin: 0 auto;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    padding-top: 20px;

    & h1.admin-arena-handle-title {
        font-size: 24px;
        font-weight: 600;
        text-align: center;
        margin-bottom: 10px;
    }

    & p.admin-arena-handle-description {
        font-size: 16px;
        font-weight: 400;
        text-align: center;
        margin-bottom: 10px;
    }
`;

const DivForButtonAggiungiArena = styled.div`
    width: 100%;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    margin: 20px 0;

    & button.btn-handle-aggiungi-arena {
        width: 300px;
        padding: 10px;
        border-radius: 5px;
        border: 1px solid white;
        background-color: ${colors.bgColorBlack2};
        color: white;
        font-size: 20px;
        text-transform: uppercase;
        cursor: pointer;
        transition: transform 0.2s ease-in-out, background-color 0.2s ease-in-out;
        
        &:hover {
            transform: scale(1.05);
            background-color: ${colors.bgColorBlack};
        }
    }
`;

const ArenasContainer = styled.div`
    width: 100%;
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
    gap: 14px;
    margin-bottom: 40px;
    
    @media (min-width: 1200px) {
        grid-template-columns: repeat(4, 1fr);
    }
    
    @media (min-width: 900px) and (max-width: 1199px) {
        grid-template-columns: repeat(3, 1fr);
    }
`;

const ArenaBox = styled.div`
    width: 100%;
    background-color: ${colors.bgColorBlack2};
    border: 2px solid rgba(255, 255, 255, 0.1);
    border-radius: 8px;
    padding: 14px;
    display: flex;
    flex-direction: column;
    gap: 6px;
    transition: transform 0.2s ease-in-out, border-color 0.2s ease-in-out;
    
    &:hover {
        transform: translateY(-2px);
        border-color: rgba(255, 255, 255, 0.2);
    }
`;

const ArenaName = styled.div`
    font-size: 16px;
    font-weight: 600;
    color: white;
    word-wrap: break-word;
`;

const ArenaMeta = styled.div`
    font-size: 13px;
    color: rgba(255, 255, 255, 0.75);
`;

const InfoBox = styled.div`
    width: 100%;
    background-color: ${colors.bgColorBlack2};
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 8px;
    padding: 14px;
    margin-top: 10px;
    color: rgba(255, 255, 255, 0.85);
    text-align: center;
`;

function ArenaHandle() {
    const loaderData = useLoaderData() as any;

    const initialArenas = useMemo(() => {
        if (!loaderData) return null;
        if (typeof loaderData === "string") return null;
        // La risposta API ha questa struttura: { data: { data: [...], current_page: ... } }
        const data = loaderData.data || loaderData;
        return Array.isArray(data.data) ? data.data : (Array.isArray(data) ? data : []);
    }, [loaderData]);

    const [arenas, setArenas] = useState<Arena[]>(initialArenas || []);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const fetchAbortRef = useRef<AbortController | null>(null);
    useEffect(() => { return () => { fetchAbortRef.current?.abort(); }; }, []);

    const fetchAllArenas = async () => {
        if (fetchAbortRef.current) fetchAbortRef.current.abort();
        fetchAbortRef.current = new AbortController();
        const { signal } = fetchAbortRef.current;
        setIsLoading(true);
        try {
            const response = await apiListArenasByPage(1) as any;
            if (signal.aborted) return;
            if (response.error) {
                console.error("Errore nel caricare le arene:", response);
                return;
            }
            // La risposta ha struttura: { data: { data: [...], current_page: ... } }
            const data = response.data || response;
            const arenasList = Array.isArray(data.data) ? data.data : (Array.isArray(data) ? data : []);
            setArenas(arenasList);
        } catch (error) {
            if (error instanceof Error && error.name === 'AbortError') return;
            console.error("Errore nel caricare le arene:", error);
        } finally {
            if (!signal.aborted) setIsLoading(false);
        }
    };

    const handleCreateArena = async (formData: FormData) => {
        const response = await apiCreateArenaV2(formData) as any;

        // Pattern API: può tornare {error}, {errors}, oppure dati (eventualmente in data)
        if (response?.errors) {
            const firstErrorKey = Object.keys(response.errors)[0];
            const firstErrorValue = response.errors[firstErrorKey];
            const message = Array.isArray(firstErrorValue) ? firstErrorValue[0] : (firstErrorValue || "Errore di validazione");
            throw new Error(message);
        }
        if (response?.error) {
            throw new Error(response.error);
        }

        // Refresh lista dopo creazione
        await fetchAllArenas();

        return response;
    };

    return (
        <CommonWrapper className="admin-arena-handle-common-wrapper">
            <Container className="admin-arena-handle-container">
                <h1 className="admin-arena-handle-title">Gestisci Arena</h1>
                <p className="admin-arena-handle-description">
                    Qui puoi visualizzare le arene e aggiungerne di nuove.
                </p>

                <DivForButtonAggiungiArena className="admin-arena-handle-button-aggiungi-arena">
                    <button
                        className="btn-handle-aggiungi-arena"
                        onClick={() => setIsModalOpen(true)}
                        disabled={isLoading}
                    >
                        {isLoading ? "Caricamento..." : "Aggiungi Arena"}
                    </button>
                </DivForButtonAggiungiArena>

                {(!arenas || arenas.length === 0) && (
                    <InfoBox className="admin-arena-handle-empty">
                        Nessuna Arena disponibile.{" "}
                        <button
                            type="button"
                            className="btn btn-link"
                            style={{ color: "gold", textDecoration: "underline" }}
                            onClick={fetchAllArenas}
                        >
                            Ricarica
                        </button>
                    </InfoBox>
                )}

                {arenas.length > 0 && (
                    <ArenasContainer className="admin-arena-handle-arenas-container">
                        {arenas.map((arena: Arena) => (
                            <ArenaBox key={arena.id} className="admin-arena-handle-arena-box">
                                <ArenaName className="admin-arena-handle-arena-name">
                                    {arena.name}
                                </ArenaName>
                                <ArenaMeta className="admin-arena-handle-arena-meta">
                                    {arena.state}
                                </ArenaMeta>
                            </ArenaBox>
                        ))}
                    </ArenasContainer>
                )}

                {isModalOpen && (
                    <CreateArenaModale
                        closeModalClick={() => setIsModalOpen(false)}
                        onSave={handleCreateArena}
                    />
                )}
            </Container>
        </CommonWrapper>
    );
}

export default ArenaHandle;
